import { useEffect, useRef, useState } from "react";

import {
  Box,
  Button,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons";

import { json2csv } from "json-2-csv";
import * as Sentry from "@sentry/react";

import ApiKeyManagement from "../../settings/ApiKeyManagement";
import Card from "../../atoms/mui/Card";
import FeatureErrorBoundary from "../../atoms/FeatureErrorBoundary";
import GrayPaper from "../../atoms/GrayPaper";

import usePlatformLayoutContext from "../../../helpers/contexts/platformLayout";
import usePermissions from "../../../helpers/hooks/usePermissions";
import { getAccountId } from "../../../helpers/accountHelpers";
import { fetchOurApi } from "../../../helpers/utils/apiCalls";
import { analyticsTrack } from "../../../helpers/analytics";

const TierComparisonCallout = () => (
  <Card
    title="You're on Tier-3 — the paid tier"
    subtitle="Your keys can call every Aclymate MCP tool, including customer-data-bearing tools that read your Navigator data."
    icon={<FontAwesomeIcon icon={faStar} />}
  />
);

const formatIsoDateTime = (isoString) => {
  if (!isoString) {
    return "—";
  }
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
};

const formatLatencyMs = (latencyMs) => {
  if (latencyMs === null || latencyMs === undefined) {
    return "—";
  }
  return `${latencyMs} ms`;
};

const buildCsvRow = (entry) => ({
  entryId: entry.entryId,
  createdAt: entry.createdAt || "",
  tool: entry.tool || "",
  sourceAgent: entry.sourceAgent || "",
  latencyMs: entry.latencyMs ?? "",
  resultHash: entry.resultHash || "",
  inputsRedacted: JSON.stringify(entry.inputsRedacted || {})
});

const downloadCsv = ({ entries, keyId }) => {
  const csv = json2csv(entries.map(buildCsvRow));
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const link = window.document.createElement("a");
  link.href = url;
  link.download = `mcp-usage-${keyId}-${new Date().toISOString().slice(0, 10)}.csv`;
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const formatInputsCellValue = (value) => {
  if (value === null || value === undefined) {
    return "—";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};

const InputsRedactedTable = ({ inputsRedacted }) => {
  const entries = Object.entries(inputsRedacted || {});
  if (entries.length === 0) {
    return (
      <Typography variant="caption" color="textSecondary">
        No inputs recorded for this call.
      </Typography>
    );
  }
  return (
    <Table size="small">
      <TableBody>
        {entries.map(([field, value]) => (
          <TableRow key={field}>
            <TableCell
              sx={{ fontFamily: "monospace", verticalAlign: "top", width: 180 }}
            >
              {field}
            </TableCell>
            <TableCell sx={{ wordBreak: "break-word" }}>
              {formatInputsCellValue(value)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const UsageRow = ({ entry, keyId, isExpanded, onToggle }) => (
  <>
    <TableRow>
      <TableCell sx={{ width: 40 }}>
        <IconButton
          size="small"
          onClick={() =>
            onToggle({
              entryId: entry.entryId,
              nextExpanded: !isExpanded,
              keyId
            })
          }
          aria-label={isExpanded ? "Collapse row" : "Expand row"}
        >
          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </TableCell>
      <TableCell>{formatIsoDateTime(entry.createdAt)}</TableCell>
      <TableCell sx={{ fontFamily: "monospace" }}>
        {entry.tool || "—"}
      </TableCell>
      <TableCell>{entry.sourceAgent || "unknown"}</TableCell>
      <TableCell align="right">{formatLatencyMs(entry.latencyMs)}</TableCell>
    </TableRow>
    <TableRow>
      <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box py={1}>
            <Typography variant="caption" color="textSecondary" gutterBottom>
              Redacted inputs
            </Typography>
            <InputsRedactedTable inputsRedacted={entry.inputsRedacted} />
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  </>
);

const buildTruncatedCaption = (truncated) =>
  truncated ? "Showing 200 most recent calls — older entries excluded." : null;

const buildCsvButtonLabel = (truncated) =>
  truncated ? "Download CSV (200 most recent)" : "Download CSV";

const isRowBeingMutated = ({ pendingAction, keyId }) =>
  Boolean(pendingAction) && pendingAction.keyId === keyId;

const ERROR_CAPTION_BY_CODE = {
  not_admin: "Only admins can view API key usage.",
  key_not_found: "This key was not found — it may have been revoked."
};
const DEFAULT_ERROR_CAPTION =
  "Couldn't load usage for this key — refresh to retry.";
const captionForErrorCode = (code) =>
  ERROR_CAPTION_BY_CODE[code] || DEFAULT_ERROR_CAPTION;

const normalizeUsageResponse = (res) => {
  const rawEntries = res && res.entries;
  const rawTruncated = res && res.truncated;
  return {
    entries: Array.isArray(rawEntries) ? rawEntries : [],
    truncated: Boolean(rawTruncated),
    entriesShapeInvalid: !Array.isArray(rawEntries)
  };
};

const PerKeyUsageDrawer = ({
  keyId,
  companyId,
  isAdmin,
  pendingAction,
  activateSnackbar
}) => {
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const [usage, setUsage] = useState({ entries: [], truncated: false });
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageErrorCode, setUsageErrorCode] = useState(null);
  const [expandedRowIds, setExpandedRowIds] = useState({});
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const rowMutating = isRowBeingMutated({ pendingAction, keyId });

  useEffect(() => {
    if (rowMutating) {
      setDrawerExpanded(false);
      requestIdRef.current += 1;
    }
  }, [rowMutating]);

  useEffect(
    () => () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
    },
    []
  );

  if (!isAdmin) {
    return null;
  }

  const loadUsage = async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setUsageLoading(true);
    setUsageErrorCode(null);
    const path = `/api-keys/${encodeURIComponent(
      keyId
    )}/usage?companyId=${encodeURIComponent(companyId)}`;
    const res = await fetchOurApi({ path, method: "GET" }).catch((err) => ({
      error: { code: "network", message: err && err.message }
    }));
    if (!mountedRef.current || requestId !== requestIdRef.current) {
      return;
    }
    if (!res || res.error) {
      const errorCode = (res && res.error && res.error.code) || "unknown";
      const errorMessage =
        (res && res.error && res.error.message) || "usage_failed";
      Sentry.captureException(new Error(errorMessage), {
        tags: { feature: "platform-api-keys-usage", errorCode },
        extra: { companyId, keyId }
      });
      setUsageErrorCode(errorCode);
      setUsageLoading(false);
      return;
    }
    const normalized = normalizeUsageResponse(res);
    if (normalized.entriesShapeInvalid) {
      Sentry.captureException(new Error("usage_entries_not_array"), {
        tags: { feature: "platform-api-keys-usage" },
        extra: { companyId, keyId, entriesType: typeof res.entries }
      });
    }
    setUsage({ entries: normalized.entries, truncated: normalized.truncated });
    setUsageLoading(false);
  };

  const onToggleDrawer = () => {
    const nextExpanded = !drawerExpanded;
    setDrawerExpanded(nextExpanded);
    if (!nextExpanded) {
      requestIdRef.current += 1;
      return;
    }
    analyticsTrack("platform_api_key_usage_drilldown_opened", {
      tier: "tier-3"
    });
    loadUsage();
  };

  const onToggleRow = ({ entryId, nextExpanded }) => {
    setExpandedRowIds((prev) => ({ ...prev, [entryId]: nextExpanded }));
    if (!nextExpanded) {
      return;
    }
    analyticsTrack("platform_api_key_usage_row_expanded", {
      tier: "tier-3",
      entryId
    });
  };

  const onDownloadCsv = () => {
    try {
      downloadCsv({ entries: usage.entries, keyId });
      analyticsTrack("platform_api_key_usage_csv_downloaded", {
        tier: "tier-3",
        rowCount: usage.entries.length
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: { feature: "platform-api-keys-usage-csv" },
        extra: { companyId, keyId, rowCount: usage.entries.length }
      });
      if (activateSnackbar) {
        activateSnackbar({
          severity: "error",
          message: "Couldn't download CSV — please try again."
        });
      }
    }
  };

  const truncatedCaption = buildTruncatedCaption(usage.truncated);
  const csvButtonLabel = buildCsvButtonLabel(usage.truncated);
  const hasEntries = usage.entries.length > 0;
  const errorCaption = usageErrorCode
    ? captionForErrorCode(usageErrorCode)
    : null;

  return (
    <GrayPaper shade="light" style={{ padding: 12, marginTop: 8 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        <Button
          onClick={onToggleDrawer}
          size="small"
          disabled={rowMutating}
          startIcon={
            drawerExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
          }
        >
          {drawerExpanded ? "Hide usage" : "View usage (30-day)"}
        </Button>
        {drawerExpanded && hasEntries && (
          <Button
            onClick={onDownloadCsv}
            size="small"
            startIcon={<FileDownloadIcon />}
          >
            {csvButtonLabel}
          </Button>
        )}
      </Stack>
      <Collapse in={drawerExpanded} timeout="auto" unmountOnExit>
        <Box mt={1}>
          {usageLoading && (
            <Typography variant="caption" color="textSecondary">
              Loading usage…
            </Typography>
          )}
          {!usageLoading && errorCaption && (
            <Typography variant="caption" color="error">
              {errorCaption}
            </Typography>
          )}
          {!usageLoading && !errorCaption && !hasEntries && (
            <Typography variant="caption" color="textSecondary">
              No calls in the last 30 days for this key.
            </Typography>
          )}
          {!usageLoading && !errorCaption && hasEntries && (
            <>
              {truncatedCaption && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                  gutterBottom
                >
                  {truncatedCaption}
                </Typography>
              )}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>When</TableCell>
                      <TableCell>Tool</TableCell>
                      <TableCell>Agent</TableCell>
                      <TableCell align="right">Latency</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usage.entries.map((entry) => (
                      <UsageRow
                        key={entry.entryId}
                        entry={entry}
                        keyId={keyId}
                        isExpanded={Boolean(expandedRowIds[entry.entryId])}
                        onToggle={onToggleRow}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      </Collapse>
    </GrayPaper>
  );
};

const ApiKeys = () => {
  const accountId = getAccountId();
  const { isAdmin } = usePermissions();
  const { activateSnackbar, isSimulation } = usePlatformLayoutContext() || {};
  const effectiveIsAdmin = isAdmin && !isSimulation;

  return (
    <Stack spacing={2}>
      <TierComparisonCallout />
      <ApiKeyManagement
        tier="tier-3"
        accountId={accountId}
        isAdmin={effectiveIsAdmin}
        activateSnackbar={activateSnackbar}
        title="Aclymate Agent Access — Tier-3"
        analyticsEventPrefix="platform_api_key"
        sentryFeaturePrefix="platform-api-keys"
        extraSlots={{
          afterActiveKeyRow: (keyRowContext) => (
            <FeatureErrorBoundary featureName="Agent Access — usage drilldown">
              <PerKeyUsageDrawer
                keyId={keyRowContext.keyId}
                companyId={accountId}
                isAdmin={effectiveIsAdmin}
                pendingAction={keyRowContext.pendingAction}
                activateSnackbar={activateSnackbar}
              />
            </FeatureErrorBoundary>
          )
        }}
      />
    </Stack>
  );
};

export default ApiKeys;
